import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Volume2, Trophy, Flame, HelpCircle, X, Sparkles, RefreshCw, Cpu, User } from 'lucide-react';
import { Difficulty, GameStats, CloudflareConfig, VoiceId, DEEPGRAM_VOICES } from '../types/game';
import { CloudflareSettingsModal } from './CloudflareSettingsModal';

interface HeaderProps {
  difficulty: Difficulty;
  voice: VoiceId;
  stats: GameStats;
  cfConfig: CloudflareConfig | null;
  isCloudflareActive: boolean;
  onSelectDifficulty: (diff: Difficulty) => void;
  onSelectVoice: (voice: VoiceId) => void;
  onSaveCfConfig: (config: CloudflareConfig | null) => void;
  onResetStats: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  difficulty,
  voice,
  stats,
  cfConfig,
  isCloudflareActive,
  onSelectDifficulty,
  onSelectVoice,
  onSaveCfConfig,
  onResetStats,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [showCfSettings, setShowCfSettings] = useState(false);

  // Show Cloudflare API Key configuration modal button ONLY in local dev environment
  const isDev = import.meta.env.DEV;

  return (
    <header className="w-full max-w-4xl flex flex-col md:flex-row items-center justify-between gap-4 py-4 px-4 bg-slate-900/60 backdrop-blur-md border border-slate-800/80 rounded-2xl shadow-lg my-2">
      {/* Brand & Logo */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-pink-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
          <Volume2 className="w-6 h-6 text-white transform -scale-x-100" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-400 via-purple-300 to-pink-400 bg-clip-text text-transparent">
            backtalk
          </h1>
          <p className="text-xs text-slate-400 font-medium">
            Listen in reverse. Guess the phrase.
          </p>
        </div>
      </div>

      {/* Stats & Controls */}
      <div className="flex flex-wrap items-center justify-center gap-2.5">
        {/* Voice Selection Dropdown */}
        <div className="flex items-center gap-1.5 bg-slate-950/80 px-2.5 py-1 rounded-xl border border-slate-800 text-xs">
          <User className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
          <select
            value={voice}
            onChange={(e) => onSelectVoice(e.target.value as VoiceId)}
            className="bg-transparent text-slate-200 font-semibold focus:outline-none cursor-pointer pr-1"
            title="Select Cloudflare Workers AI Voice"
          >
            {DEEPGRAM_VOICES.map((v) => (
              <option key={v.id} value={v.id} className="bg-slate-900 text-white">
                {v.name} ({v.gender}{v.accent ? ` ${v.accent}` : ''})
              </option>
            ))}
          </select>
        </div>

        {/* Difficulty Selector */}
        <div className="flex items-center bg-slate-950/80 p-1 rounded-xl border border-slate-800 text-xs">
          {(['easy', 'medium', 'hard'] as Difficulty[]).map((diff) => (
            <button
              key={diff}
              onClick={() => onSelectDifficulty(diff)}
              className={`px-2.5 py-1 rounded-lg capitalize font-bold transition-all ${
                difficulty === diff
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {diff}
            </button>
          ))}
        </div>

        {/* Streak Counter */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold shadow-sm">
          <Flame className="w-4 h-4 fill-amber-400 text-amber-500 animate-bounce-short" />
          <span>{stats.streak} Streak</span>
        </div>

        {/* Solved Counter */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs font-bold shadow-sm">
          <Trophy className="w-4 h-4 text-indigo-400" />
          <span>{stats.solved} Solved</span>
        </div>

        {/* Cloudflare Workers AI API Key Settings Button - DEV ONLY */}
        {isDev && (
          <button
            onClick={() => setShowCfSettings(true)}
            className={`p-2 rounded-xl border transition-colors flex items-center justify-center ${
              isCloudflareActive || (cfConfig?.accountId && cfConfig?.apiToken)
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-sm shadow-emerald-500/20'
                : 'bg-slate-800 hover:bg-slate-700 text-indigo-300 border-slate-700'
            }`}
            title="Cloudflare AI Dev Settings (Dev Mode Only)"
          >
            <Cpu className="w-5 h-5" />
          </button>
        )}

        {/* How to Play Help Icon */}
        <button
          onClick={() => setShowHelp(true)}
          className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors border border-slate-700"
          title="How to Play"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Cloudflare Settings Modal (Dev Only) - Rendered via React Portal to document.body */}
      {isDev && showCfSettings && (
        <CloudflareSettingsModal
          config={cfConfig}
          isCloudflareActive={isCloudflareActive}
          onSaveConfig={onSaveCfConfig}
          onClose={() => setShowCfSettings(false)}
        />
      )}

      {/* How to Play Help Modal - Rendered via React Portal to document.body */}
      {showHelp &&
        createPortal(
          <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md overflow-y-auto p-4 sm:p-6 md:p-8 flex items-center justify-center animate-fade-in">
            <div className="bg-slate-900 border border-slate-800/90 w-full max-w-lg sm:max-w-xl rounded-2xl p-6 sm:p-8 shadow-2xl relative flex flex-col gap-5 my-auto max-h-[85vh] overflow-y-auto">
              <button
                onClick={() => setShowHelp(false)}
                className="absolute top-5 right-5 text-slate-400 hover:text-white p-1.5 rounded-lg bg-slate-800/60 hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-2.5 text-indigo-400 font-bold text-xl pr-8">
                <Sparkles className="w-6 h-6 shrink-0" />
                <span>How to Play Backtalk</span>
              </div>

              <ul className="text-sm sm:text-base text-slate-300 flex flex-col gap-3 list-disc pl-5 leading-relaxed font-normal">
                <li>
                  Press <strong className="text-indigo-400 font-semibold">Play Reverse Phrase</strong> to hear speech spoken <em>backwards</em>.
                </li>
                <li>
                  Change voices (Angus, Asteria, Zeus, Luna, etc.) using the voice dropdown menu.
                </li>
                <li>
                  Adjust playback speed with the speed slider — pitch is automatically preserved!
                </li>
                <li>
                  Type your guess directly into the blanks. Backspace and arrow keys let you edit freely.
                </li>
                <li>
                  Press <strong className="text-amber-400 font-semibold">Ask for Hint</strong> to fill in a single letter blank slot.
                </li>
              </ul>

              <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between gap-3 mt-2">
                <button
                  onClick={onResetStats}
                  className="text-xs sm:text-sm text-rose-400 hover:text-rose-300 flex items-center gap-1.5 font-medium transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset Stats
                </button>
                <button
                  onClick={() => setShowHelp(false)}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-sm shadow-md shadow-indigo-600/30 transition-all active:scale-95"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};
